import { useQuery } from '@tanstack/react-query';
import { post } from '@/lib/api/client';

/**
 * Movement — what sold, grouped by where the product sits in the hierarchy.
 *
 * Four legacy pages (`/finance/divisions`, `/finance/departments`,
 * `/finance/store/departments`, `/finance/store/categories`) called four pairs
 * of endpoints that differ only in their `GROUP BY`. They are one report with a
 * level selector, so they are one module.
 *
 *   division    →  `Item.SuggestedDepartment`      (a free-text string)
 *   group       →  `BusinessItem.Department`       (a free-text string)
 *   department  →  `Section.SuperSection`          (an entity, id + names)
 *   category    →  `Section`                       (an entity, inside a department)
 *
 * Every level takes a `Filter` that narrows the *items* call to one row of the
 * grouped call — string levels match the name, entity levels the encoded id.
 * That is what makes the drill-down possible without a backend change.
 */

// ------------------------------------------------------------------ levels

export type MovementLevel = 'division' | 'group' | 'department' | 'category';

/**
 * `category` is the one level that cannot stand alone: both of its endpoints
 * open with `encoder.Decode(model.DepartmentId)` and filter on it before
 * anything else, so a category list is always a list *within one department*.
 */
export const NEEDS_DEPARTMENT: MovementLevel = 'category';

const ROOT: Record<MovementLevel, string> = {
  division: 'analytics/reporting/divisionsales/withmodifiers/search',
  group: 'analytics/reporting/departmentsales/withmodifiers/search',
  department: 'analytics/reporting/storedepartmentsales/withmodifiers/search',
  category: 'analytics/reporting/storecategorysales/withmodifiers/search',
};

/**
 * Levels whose rows are named entities rather than free text.
 *
 * These fill `departmentId` / `departmentNameEn` / `departmentNameEs` and
 * leave `department` empty; the string levels do the exact opposite. Reading
 * the wrong pair is how the legacy pages ended up with blank first columns.
 */
const IS_ENTITY: Record<MovementLevel, boolean> = {
  division: false,
  group: false,
  department: true,
  category: true,
};

/**
 * The literal the API substitutes for a division or group when the line was
 * filled with a substitute product:
 *
 *   Department = string.IsNullOrWhiteSpace(t.SubstituteUpc)
 *       ? t.BusinessItem.Item.SuggestedDepartment
 *       : "testing"
 *
 * It is a real bucket carrying real revenue, so it is shown rather than
 * dropped — but under a name that says what it is. Recorded in
 * `docs/api-findings-movement.md`.
 */
export const SUBSTITUTE_BUCKET = 'testing';

// -------------------------------------------------------------------- wire

interface GroupedResultDto {
  extended: number;
  profit: number;
  profitMargin: number;
  cost: number;
  stateTax: number;
  municipalTax: number;
  total: number;
  orders: number;
  itemCount: number;
  itemAmount: number;
  department: string | null;
  departmentNameEs: string | null;
  departmentNameEn: string | null;
  departmentId: string | null;
}

interface SearchDto {
  groupedResult: GroupedResultDto[] | null;
  totalOrders: number;
  totalExtended: number;
  itemCount: number;
  totalItems: number;
  totalSold: number;
  total: number;
}

export interface ModifierDto {
  name: string | null;
  totalSales: number;
  priceTotal: number;
  priceAverage: number;
  amount: number;
  stateTaxTotal: number;
  municipalTaxTotal: number;
}

export interface MovementItem {
  upc: string | null;
  brand: string | null;
  name: string | null;
  description: string | null;
  department: string | null;
  amount: number;
  orderCount: number;
  profit: number;
  profitMargin: number;
  priceTotal: number;
  priceAverage: number;
  stateTaxTotal: number;
  municipalTaxTotal: number;
  totalSales: number;
  cost: number;
  modifierSubtotal: number;
  modifierStateTax: number;
  modifierMunicipalTax: number;
  modifiers: ModifierDto[] | null;
}

// ------------------------------------------------------------------- rows

export interface MovementRow {
  /** What `Filter` must carry to drill into this row. Null when it cannot. */
  filter: string | null;
  /** Already resolved for the reader's language. */
  label: string;
  /** True for the "substituted items" bucket, which is not a real division. */
  substituted: boolean;
  extended: number;
  /** Derived — see `modifiersOf`. */
  modifiers: number;
  total: number;
  stateTax: number;
  municipalTax: number;
  cost: number;
  profit: number;
  /** A fraction, as the API returns it. */
  profitMargin: number;
  orders: number;
  lines: number;
  units: number;
}

export interface MovementReport {
  rows: MovementRow[];
  orders: number;
  /** Order *lines*, not distinct products — the API's `ItemCount` is `Count()`. */
  lines: number;
  units: number;
  /**
   * Regular items at `price × amount`, before tax and before modifiers.
   *
   * This is the figure that reconciles with the dashboard: `trends/categories`
   * computes `CurrentSalesTotal` the same way over the same population.
   */
  merchandise: number;
  /** Merchandise + tax + modifiers. Nothing on the dashboard matches it. */
  total: number;
}

/**
 * What the modifiers on a row came to.
 *
 * The API never returns it, but it is recoverable: `Total` is built as
 * `extended + municipalTax + stateTax + modifierTotal`, and the three
 * addends are all on the row. Worth recovering because it is the entire
 * discrepancy between this report and the dashboard's "Where it sold" —
 * `trends/categories` filters `Type == Regular` and so counts none of it.
 */
function modifiersOf(row: GroupedResultDto): number {
  return row.total - row.extended - row.stateTax - row.municipalTax;
}

function toRow(
  dto: GroupedResultDto,
  level: MovementLevel,
  spanish: boolean,
  fallback: string,
  substitutedLabel: string
): MovementRow {
  const entity = IS_ENTITY[level];
  const name = entity
    ? ((spanish ? dto.departmentNameEs : dto.departmentNameEn) ??
      dto.departmentNameEn ??
      dto.departmentNameEs)
    : dto.department;
  const substituted = !entity && dto.department === SUBSTITUTE_BUCKET;

  return {
    // A blank string level cannot be drilled: `Filter` is read through
    // `IsNullOrWhiteSpace`, so sending "" asks for everything rather than for
    // the unnamed bucket. The page disables the row instead of lying about it.
    filter: entity ? dto.departmentId : (dto.department || null),
    label: substituted ? substitutedLabel : (name?.trim() || fallback),
    substituted,
    extended: dto.extended,
    modifiers: modifiersOf(dto),
    total: dto.total,
    stateTax: dto.stateTax,
    municipalTax: dto.municipalTax,
    cost: dto.cost,
    profit: dto.profit,
    profitMargin: dto.profitMargin,
    orders: dto.orders,
    lines: dto.itemCount,
    units: dto.itemAmount,
  };
}

// ---------------------------------------------------------------- requests

export interface MovementQuery {
  level: MovementLevel;
  /** `yyyy-MM-dd`. */
  start: string;
  end: string;
  /** Encoded id, or null for every store. */
  businessId: string | null;
  /** Required at the `category` level, ignored everywhere else. */
  departmentId: string | null;
}

/**
 * Dates go out as the raw picked days.
 *
 * These endpoints take `model.StartDate.Date` and `model.EndDate.Date
 * .AddDays(1)` and apply `-TimezoneOffset` themselves, so the last day is
 * already included and any client-side shift would be applied twice. This is
 * the opposite of the dashboard, whose v2 endpoints use
 * `EndDate.Date.AddSeconds(-1)` and therefore need the extra day added here.
 * Same picked range, two different payloads, one answer.
 */
function body(query: MovementQuery, filter?: string | null) {
  return {
    StartDate: query.start,
    EndDate: query.end,
    BusinessId: query.businessId,
    ...(query.level === NEEDS_DEPARTMENT
      ? { DepartmentId: query.departmentId }
      : null),
    ...(filter === undefined ? null : { Filter: filter }),
  };
}

/** True when the query has everything its level needs to run. */
export function isRunnable(query: MovementQuery): boolean {
  return query.level !== NEEDS_DEPARTMENT || Boolean(query.departmentId);
}

export function useMovement(
  query: MovementQuery,
  labels: { fallback: string; substituted: string },
  spanish: boolean
) {
  return useQuery({
    queryKey: ['movement', query, spanish],
    enabled: isRunnable(query),
    staleTime: 60_000,
    queryFn: async (): Promise<MovementReport> => {
      const dto = await post<SearchDto>(ROOT[query.level], body(query));
      return {
        rows: (dto.groupedResult ?? []).map((row) =>
          toRow(row, query.level, spanish, labels.fallback, labels.substituted)
        ),
        orders: dto.totalOrders,
        lines: dto.itemCount,
        units: dto.totalSold,
        merchandise: dto.totalExtended,
        total: dto.total,
      };
    },
  });
}

/**
 * The products behind one row — or behind the whole level, when `filter` is
 * null and the user asked for everything.
 *
 * Kept lazy: this is the expensive half of the report (the service pulls every
 * matching line into memory to attach modifiers), and the legacy pages made it
 * an explicit "Load items" button for that reason. Here it is the drill-down,
 * which is the same bargain with a better name.
 */
export function useMovementItems(
  query: MovementQuery,
  filter: string | null,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['movement', 'items', query, filter],
    enabled: enabled && isRunnable(query),
    staleTime: 60_000,
    queryFn: () =>
      post<MovementItem[]>(`${ROOT[query.level]}/items`, body(query, filter)),
    select: (rows) => rows ?? [],
  });
}
